import React, { useMemo, useState } from 'react';
import Grid from '@mui/material/Grid2';
import { AnimatePresence } from 'framer-motion';
import { useSelector } from 'react-redux';
import { useLanguage } from 'Context/LanguageContext';
import RoleUtils from 'FielderUtils/roles/roleUtils';
import { selectAccountPreferences, selectUser } from '@core/store/modules/session/sessionSelectors';
import { FielderRootContainerXXL } from '~/FielderElements/FielderElements.component';
import { StyleFielderTitleTypography } from '~/FielderElements/FielderElements.styles';
import { MotionGrid } from './Dashboard.styles';
import { CATEGORIES } from './Dashboard.constants';
import TaskDashboard from './components/TaskDashboard.old';
import AgentDashboard from './components/AgentDashboard';
import TaskToCashDashboard from '~/TaskToCashDashboard/TaskToCashDashboard.component';
import { FielderSmartNav } from '~/FielderElements/Utils/SmartTabNav';

const CategoryAnimations = {
	transition: { duration: 0.2 },
	initial: { y: 10, opacity: 0 },
	animate: { y: 0, opacity: 1 },
	exit: { y: -10, opacity: 0 },
};

const Dashboard = () => {
	const lan = useLanguage();
	const user = useSelector(selectUser);
	const { allowAdminToViewTaskToCash } = useSelector(selectAccountPreferences);
	const isClient = useMemo(() => RoleUtils.isUserClient(user), [user]);
	const isAdmin = useMemo(() => RoleUtils.isUserAdministrator(user), [user]);
	const isSuperAdmin = useMemo(() => RoleUtils.isUserSuperAdministrator(user), [user]);
	const [selectedCategory, setSelectedCategory] = useState(CATEGORIES.tasks);
	const navItems = useMemo(() => {
		const items = [{ id: CATEGORIES.tasks, label: lan.tasks }];
		if (!isClient) items.push({ id: CATEGORIES.agents, label: lan.agents });
		if (isSuperAdmin || (isAdmin && allowAdminToViewTaskToCash)) {
			items.push({ id: CATEGORIES.taskToCash, label: lan.taskToCash });
		}
		return items;
	}, [isClient, isAdmin, isSuperAdmin, allowAdminToViewTaskToCash, lan.tasks, lan.agents, lan.taskToCash]);

	const DashboardCategory = useMemo(() => {
		if (selectedCategory === CATEGORIES.tasks) return <TaskDashboard />;
		if (selectedCategory === CATEGORIES.agents) return <AgentDashboard />;
		if (selectedCategory === CATEGORIES.taskToCash) return <TaskToCashDashboard />;
		return null;
	}, [selectedCategory]);

	return (
		<FielderRootContainerXXL>
			<Grid container direction="row" spacing={2} alignItems="center">
				<Grid size={12}>
					<StyleFielderTitleTypography>{lan.dashboard.toUpperCase()}</StyleFielderTitleTypography>
				</Grid>
				<Grid size={12}>
					<FielderSmartNav
						items={navItems}
						value={selectedCategory}
						onChange={setSelectedCategory}
						variant="standard"
						centered={false}
						sx={{ minWidth: 360 }}
					/>
				</Grid>
			</Grid>

			<AnimatePresence mode="wait">
				<MotionGrid
					container
					direction="row"
					alignItems="stretch"
					spacing={1}
					key={selectedCategory}
					variants={CategoryAnimations}
					initial="initial"
					animate="animate"
					exit="exit"
					transition={CategoryAnimations.transition}
					className="mt-2"
				>
					<Grid size={12}>{DashboardCategory}</Grid>
				</MotionGrid>
			</AnimatePresence>
		</FielderRootContainerXXL>
	);
};

export default Dashboard;
//Original code:
/*import React, { useState } from 'react';
import { useLanguage } from 'FielderUtils/Contexts/LanguageContext';
import { CategoryButtonGroup, CategoryButton, MotionGrid } from './Dashboard.styles';
import { AnimatePresence } from 'framer-motion';
import TaskDashboard from './components/TaskDashboard.old';
import AgentDashboard from './components/AgentDashboard';
import TaskToCashDashboard from '~/TaskToCashDashboard/TaskToCashDashboard.component';
import { CATEGORIES } from './Dashboard.constants';
import RoleUtils from 'FielderUtils/roles/roleUtils';
import { useSelector } from 'react-redux';
import { selectAccountPreferences, selectUser } from '@core/store/modules/session/sessionSelectors';
import Grid from '@mui/material/Grid2';
import { StyleFielderTitleTypography } from '~/FielderElements/FielderElements.styles';
import { FielderRootContainerXXL } from '~/FielderElements/FielderElements.component';

const CategoryAnimations = {
	transition: { duration: 0.2 },
	initial: { y: 10, opacity: 0 },
	animate: { y: 0, opacity: 1 },
	exit: { y: -10, opacity: 0 },
};
const Dashboard = () => {
	const lan = useLanguage();
	const user = useSelector(selectUser);
	const { allowAdminToViewTaskToCash } = useSelector(selectAccountPreferences);
	const isClient = React.useMemo(() => RoleUtils.isUserClient(user), [user]);
	const isAdmin = React.useMemo(() => RoleUtils.isUserAdministrator(user), [user]);
	const isSuperAdmin = React.useMemo(() => RoleUtils.isUserSuperAdministrator(user), [user]);
	const dashboardTitle = lan.dashboard.toUpperCase();
	const [selectedCategory, setSelectedCategory] = useState(CATEGORIES.tasks);
	const handleChangeCategory = (e, newCategory) => {
		if (!newCategory) return;
		setSelectedCategory(newCategory);
	};
	const DashboardCategory = React.useMemo(() => {
		if (selectedCategory === CATEGORIES.tasks) {
			return <TaskDashboard />;
		} else if (selectedCategory === CATEGORIES.agents) {
			return <AgentDashboard />;
		} else if (selectedCategory === CATEGORIES.taskToCash) {
			return <TaskToCashDashboard />;
		}
		return null;
	}, [selectedCategory]);
	return (
		<FielderRootContainerXXL>
			<Grid container direction='row' spacing={2} alignContent='center' alignItems='center'>
				<Grid size={'auto'}>
					<StyleFielderTitleTypography>{dashboardTitle}</StyleFielderTitleTypography>
				</Grid>
				<Grid size={'auto'}>
					<CategoryButtonGroup size='medium' value={selectedCategory} onChange={handleChangeCategory} exclusive>
						<CategoryButton value={CATEGORIES.tasks}>{lan.tasks}</CategoryButton>
						{!isClient && <CategoryButton value={CATEGORIES.agents}>{lan.agents}</CategoryButton>}
						{(isSuperAdmin || (isAdmin && allowAdminToViewTaskToCash)) && (
							<CategoryButton value={CATEGORIES.taskToCash}>{lan.taskToCash}</CategoryButton>
						)}
					</CategoryButtonGroup>
				</Grid>
			</Grid>
			<AnimatePresence exitBeforeEnter>
				<MotionGrid
					container
					direction='row'
					alignItems='stretch'
					spacing={1}
					key={selectedCategory}
					variants={CategoryAnimations}
					initial='initial'
					animate='animate'
					exit='exit'
					transition={CategoryAnimations.transition}
					className='mt-2'
				>
					<Grid size={12}>
						{DashboardCategory}
					</Grid>
				</MotionGrid>
			</AnimatePresence>
		</FielderRootContainerXXL>
	);
};
export default Dashboard;
*/
